import { ChevronLeft, PencilLine, MapPin, Phone, Star, FileText } from "lucide-react";
import headerBg from "../../assets/images/doctorDetails-bg.png";
import { ClinicDetailsType } from "../../types/clinic";
import DoctorHealthPlaceholder from "../../assets/images/default_avatar.jpg";
import { useAuth } from "../../context/AuthContext";

interface ClinicDetailsPropsType {
  onClose?: () => void;
  clinic: ClinicDetailsType;
  onEdit?: (clinic: ClinicDetailsType) => void;
}

export function ClinicDetails({ onClose, clinic, onEdit }: ClinicDetailsPropsType) {
  const { user } = useAuth();

  return (
    <section className="w-full  mx-auto px-4 py-6 animate-fadeIn">
      {/* Heading and back button box */}
      <h3 className="flex items-center justify-start gap-4 mb-6">
        <button
          className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-all duration-200 hover:shadow-md"
          onClick={onClose}>
          <ChevronLeft className="w-5 h-5" />
        </button>
        <span className="font-semibold text-2xl text-gray-800">Clinic Details</span>
      </h3>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
        <div
          className="w-full h-[200px] bg-cover bg-center bg-no-repeat relative"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${headerBg})`,
          }}>
          {/* Edit profile button */}
          {user?.role?.toLocaleLowerCase() === "admin" && (
            <button
              onClick={() => onEdit?.(clinic)}
              className="absolute top-4 right-4 bg-white hover:bg-gray-50 flex items-center justify-center gap-2 font-medium text-sm border border-gray-200 rounded-lg px-4 py-2 text-primary shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
              <PencilLine className="w-4 h-4" />
              Edit Clinic
            </button>
          )}

          {/*Profile and Clinic details */}
          <div className="absolute -bottom-20 left-0 right-0 px-6">
            <div className="flex items-end gap-6">
              {/* Profile Image */}
              <div className="h-32 w-32 rounded-2xl border-4 border-white shadow-lg overflow-hidden transition-transform duration-300 hover:scale-105">
                <img
                  src={
                    clinic.profileImage instanceof File ? URL.createObjectURL(clinic.profileImage) : clinic.profileImage || DoctorHealthPlaceholder
                  }
                  alt={clinic.name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Clinic details */}
              <div className="flex-1 pb-4">
                <h2 className="text-2xl font-bold text-gray-700 mb-1">{clinic.name}</h2>
                <p className="text-lg text-gray-700 font-medium">{clinic.supervisorName}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Details */}
        <div className="pt-24 px-6 pb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Registration Details */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-gray-700 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                <FileText className="w-5 h-5 text-primary" />
                <div>
                  <span className="font-semibold">Registration No: </span>
                  <span>{clinic.registrationNumber}</span>
                </div>
              </div>
              <div className="flex items-center gap-3 text-gray-700 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                <FileText className="w-5 h-5 text-primary" />
                <div>
                  <span className="font-semibold">Drug License No: </span>
                  <span>{clinic.drugLicenseNumber}</span>
                </div>
              </div>
            </div>

            {/* Contact Details */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-gray-700 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                <Phone className="w-5 h-5 text-primary" />
                <span>{clinic.mobileNumber}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                <FileText className="w-5 h-5 text-primary" />
                <div>
                  <span className="font-semibold">Fssai No: </span>
                  <span>{clinic?.fssaiNumber}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="mt-6 flex items-start gap-3 text-gray-700 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200">
            <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
            <div className="space-y-1">
              <p className="font-medium">{clinic.address}</p>
              <p className="text-gray-500">
                {clinic.city}, {clinic.state}, {clinic.pincode}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
